import { inject, Injectable, Signal, signal } from '@angular/core';
import { Company } from "@models/companies/company";
import { Document } from "@models/documents/document";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { CompanyChangeOfficerType } from "@models/relationships/company-change-officer-type";
import { AppointmentType, TrustChangeRelationship } from "@models/trusts/trust-change-relationship";
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import {
  BulkCompanyChangeAddressComponent,
  BulkCompanyChangeOfficerAppointComponent,
  BulkCompanyChangeOfficerCeaseComponent,
  BulkCompanyChangePartyNameComponent,
  BulkCompanyForm361Component
} from "../asic-forms/bulks";
import {
  AsicFormRa01Component,
  AsicFormRa04Component,
  CompanyChangeAddressComponent,
  CompanyChangeDeregisterComponent,
  CompanyChangeHoldingCompanyComponent,
  CompanyChangeName,
  CompanyChangeNameComponent,
  CompanyChangeOfficerAppointComponent,
  CompanyChangeOfficerCeaseComponent,
  CompanyChangeOfficerResignationComponent,
  CompanyChangePartyNameComponent,
  CompanyChangeSpecialPurposeComponent,
  CompanyChangeTypeComponent,
  CompanyDividendStatementComponent,
  CompanyForm361Component,
  CompanyForm362Component,
  CompanyMistakeCorrectionComponent,
  CompanyRegistrationComponent,
  CompanyWithdrawLodgedDocumentChangeComponent,
  Form280Component,
  Form281Component,
  Form388Component,
  Form410bTransactionComponent,
  Form410FTransactionComponent
} from "../asic-forms/company";
import {
  CompanyChangeSpecialPurpose
} from "../asic-forms/company/company-change-special-purpose/company-change-special-purpose.model";
import {
  CompanySecurityCancellationComponent,
  CompanySecurityConsolidationSubdivisionComponent,
  CompanySecurityIssueComponent,
  CompanySecurityTransferComponent,
} from "../asic-forms/shares";
import {
  HistoricalTransactionComponent
} from "../asic-forms/shares/historical-transaction/historical-transaction.component";
import { BaseBulkStepperFormComponent, BaseStepperFormComponent } from "../asic-forms/stepper-form";
import {
  FormChangeOfAppointorComponent,
  FormChangeOfTrusteeComponent,
  FormTrustDistributionComponent,
  TrustDiscretionaryEstablishmentComponent,
  TrustUnitEstablishmentComponent
} from "../asic-forms/trusts";
import {
  TrustDistribution
} from "../asic-forms/trusts/form-distribution-resolution-or-minutes/form-distribution-resolution-or-minutes.model";
import {
  AsicFormRA01,
  AsicFormRA04,
  CompanyChangeAddress,
  CompanyChangeDeregister,
  CompanyChangeHoldingCompany,
  CompanyChangeOfficer,
  CompanyChangeOfficerResignation,
  CompanyChangePartyName,
  CompanyChangeType,
  CompanyDividendStatement,
  CompanyForm280,
  CompanyForm281,
  CompanyForm361,
  CompanyForm362,
  CompanyForm388,
  CompanyMistakeCorrection,
  CompanyRegistration,
  CompanyWithdrawLodgedDocumentChange,
  Form410BTransaction,
  Form410FTransaction
} from "../models/company";
import {
  CompanySecurityCancellation,
  CompanySecurityConsolidationSubdivision,
  CompanySecurityIssue,
  CompanySecurityTransfer,
  HistoricalTransaction
} from "../models/shares";
import { TrustDiscretionaryEstablishment } from "../models/trusts/trust-discretionary-establishment.model";
import { TrustUnitEstablishment } from "../models/trusts/trust-unit-establishment.model";

@Injectable({
  providedIn: 'root'
})
export class ModalFormsService {
  private modalService = inject(NgbModal);

  static readonly commonCloseConfirmationMessage = 'Are you sure you want to close the form? The data will be cleared.';
  static readonly asicFormsModalClasses = {
    windowClass: 'asic-form-modal-window',
    modalDialogClass: 'asic-form-modal-dialog'
  };

  private _modalOpened = signal(false);
  private openedForms: NgbModalRef[] = [];

  static formsDictionary: Record<string, (change: EntityChangeData) => new () => BaseStepperFormComponent<unknown, EntityChangeData>> = {
    [CompanyChangeAddress.$type.toString()]: () => CompanyChangeAddressComponent,
    [CompanyChangeHoldingCompany.$type.toString()]: () => CompanyChangeHoldingCompanyComponent,
    [CompanyChangeName.$type.toString()]: () => CompanyChangeNameComponent,
    [CompanyChangePartyName.$type.toString()]: () => CompanyChangePartyNameComponent,
    [CompanyChangeOfficer.$type.toString()]: (change) => (change as CompanyChangeOfficer).actionType === CompanyChangeOfficerType.Appointment ? CompanyChangeOfficerAppointComponent : CompanyChangeOfficerCeaseComponent,
    [CompanyChangeSpecialPurpose.$type.toString()]: () => CompanyChangeSpecialPurposeComponent,
    [AsicFormRA01.$type.toString()]: () => AsicFormRa01Component,
    [AsicFormRA04.$type.toString()]: () => AsicFormRa04Component,
    [CompanyDividendStatement.$type.toString()]: () => CompanyDividendStatementComponent,
    [CompanyWithdrawLodgedDocumentChange.$type.toString()]: () => CompanyWithdrawLodgedDocumentChangeComponent,
    [CompanyForm361.$type.toString()]: () => CompanyForm361Component,
    [CompanyChangeOfficerResignation.$type.toString()]: () => CompanyChangeOfficerResignationComponent,
    [Form410BTransaction.$type.toString()]: () => Form410bTransactionComponent,
    [Form410FTransaction.$type.toString()]: () => Form410FTransactionComponent,
    [CompanyMistakeCorrection.$type.toString()]: () => CompanyMistakeCorrectionComponent,
    [CompanyChangeDeregister.$type.toString()]: () => CompanyChangeDeregisterComponent,
    [CompanyForm362.$type.toString()]: () => CompanyForm362Component,
    [CompanyChangeType.$type.toString()]: () => CompanyChangeTypeComponent,
    [CompanyForm280.$type.toString()]: () => Form280Component,
    [CompanyForm281.$type.toString()]: () => Form281Component,
    [CompanyForm388.$type.toString()]: () => Form388Component,
    [CompanySecurityCancellation.$type.toString()]: () => CompanySecurityCancellationComponent,
    [CompanySecurityIssue.$type.toString()]: () => CompanySecurityIssueComponent,
    [CompanySecurityTransfer.$type.toString()]: () => CompanySecurityTransferComponent,
    [CompanySecurityConsolidationSubdivision.$type.toString()]: () => CompanySecurityConsolidationSubdivisionComponent,
    [CompanyRegistration.$type.toString()]: () => CompanyRegistrationComponent,
    [HistoricalTransaction.$type.toString()]: () => HistoricalTransactionComponent,
  };

  static formsBulkDictionary: Record<string, (change: EntityChangeData) => new () => BaseBulkStepperFormComponent<unknown, EntityChangeData>> = {
    [CompanyChangeAddress.$type.toString()]: () => BulkCompanyChangeAddressComponent,
    [CompanyChangePartyName.$type.toString()]: () => BulkCompanyChangePartyNameComponent,
    [CompanyChangeOfficer.$type.toString()]: (change) => (change as CompanyChangeOfficer).actionType === CompanyChangeOfficerType.Appointment ? BulkCompanyChangeOfficerAppointComponent : BulkCompanyChangeOfficerCeaseComponent,
    [CompanyForm361.$type.toString()]: () => BulkCompanyForm361Component,
  };

  static trustsFormsDictionary: Record<string, (change: EntityChangeData) => new () => BaseStepperFormComponent<unknown, EntityChangeData>> = {
    [TrustDistribution.$type]: () => FormTrustDistributionComponent,
    [TrustChangeRelationship.$type]: (change) => {
      const trustChangeRelationshipChange = change as TrustChangeRelationship;

      switch (trustChangeRelationshipChange.appointmentType) {
        case AppointmentType.Trustees:
          return FormChangeOfTrusteeComponent;
        case AppointmentType.Appointor:
          return FormChangeOfAppointorComponent;
        // case AppointmentType.Settlor:
        default:
          throw new Error('[trustsFormsDictionary.TrustChangeRelationship] Wrong appointment type');
      }
    },
    [TrustDiscretionaryEstablishment.$type]: (change) => TrustDiscretionaryEstablishmentComponent,
    [TrustUnitEstablishment.$type]: (change) => TrustUnitEstablishmentComponent,
  };

  public openModalWithCompany<T>(
    change: EntityChangeData,    // instance of changes in form
    company: Company,            // instance of company profile
    isEdit = false,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {}
  ): NgbModalRef {
    const modalRef = change.fillModalWithCompany(
      this.openModal(ModalFormsService.formsDictionary[change.$type](change), { ...ModalFormsService.asicFormsModalClasses, ...modalOptions }),
      company,
      isEdit
    );

    const instance = modalRef.componentInstance as T;

    Object.entries(props).forEach(([key, value]) => {
      instance[key] = value;
    });

    return modalRef;
  }

  public openModalWithDocument<T>(
    document: Document,          // instance of document
    actualCompany: Company,
    change: EntityChangeData,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {}
  ): NgbModalRef {
    change.changeDate = DatepickerHelper.toDate(change.changeDate);
    const modalRef = change.fillModal(
      this.openModal(ModalFormsService.formsDictionary[change.$type](change), { ...ModalFormsService.asicFormsModalClasses, ...modalOptions }),
      document,
      actualCompany
    );

    const instance = modalRef.componentInstance as T;

    Object.entries(props).forEach(([key, value]) => {
      instance[key] = value;
    });

    return modalRef;
  }

  public openModal<T>(componentClass: new () => T, options: NgbModalOptions = {}, closeConfirmationMessage = ModalFormsService.commonCloseConfirmationMessage): NgbModalRef {
    this._modalOpened.set(true);
    let modalRef!: NgbModalRef;

    const beforeDismiss = () => {
      if (!closeConfirmationMessage) return true;

      const instance = modalRef.componentInstance as BaseStepperFormComponent<any, any>;
      return instance.forceClose || !instance.isLoading && confirm(closeConfirmationMessage);
    };

    modalRef = this.modalService.open(
      componentClass,
      {
        beforeDismiss,
        ...options
      });

    this.openedForms.push(modalRef);
    modalRef.result
      .then(() => this._modalOpened.set(false), () => this._modalOpened.set(false))
      .finally(() => this.openedForms = this.openedForms.filter((ref) => ref.componentInstance));

    return modalRef;
  }

  ///TODO Refactor, bulk does not use company, new interface should be implemented
  public openModalWithBulkCompany<T>(
    change: EntityChangeData,    // instance of changes in form
    company: Company,            // instance of company profile
    isEdit = false,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {}
  ): NgbModalRef {
    const modalRef = change.fillModalWithCompany(
      this.openBulkModal(ModalFormsService.formsBulkDictionary[change.$type](change), modalOptions),
      company,
      isEdit
    );

    const instance = modalRef.componentInstance as T;

    Object.entries(props).forEach(([key, value]) => {
      instance[key] = value;
    });

    return modalRef;
  }

  public openBulkModal<T>(componentClass: new () => T, options: NgbModalOptions = {}): NgbModalRef {
    let modalRef!: NgbModalRef;

    const beforeDismiss = () => {
      const instance = modalRef.componentInstance as BaseBulkStepperFormComponent<any, any>;

      if ('forceClose' in instance && instance.forceClose)
        return instance.forceClose;

      return !instance.isLoading() && confirm('All provided data will be deleted. Are you certain you want to close the form?');
    };

    modalRef = this.openModal(
      componentClass,
      {
        beforeDismiss,
        ...options
      }
    );

    return modalRef;
  }

  public openTrustModalWithCompany<T>(
    change: EntityChangeData,    // instance of changes in form
    company: Company,            // instance of company profile
    isEdit = false,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {},
    closeConfirmationMessage = ModalFormsService.commonCloseConfirmationMessage
  ): NgbModalRef {
    const modalRef = change.fillModalWithCompany(
      this.openModal(ModalFormsService.trustsFormsDictionary[change.$type](change), modalOptions, closeConfirmationMessage),
      company,
      isEdit
    );

    const instance = modalRef.componentInstance as T;

    Object.entries(props).forEach(([key, value]) => {
      instance[key] = value;
    });

    return modalRef;
  }

  public openTrustModalWithDocument<T>(
    document: Document,          // instance of document
    actualCompany: Company,
    change: EntityChangeData,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {},
    closeConfirmationMessage = ModalFormsService.commonCloseConfirmationMessage
  ): NgbModalRef {
    change.changeDate = DatepickerHelper.toDate(change.changeDate);
    const modalRef = change.fillModal(
      this.openModal(ModalFormsService.trustsFormsDictionary[change.$type](change), modalOptions, closeConfirmationMessage),
      document,
      actualCompany
    );

    const instance = modalRef.componentInstance as T;

    Object.entries(props).forEach(([key, value]) => {
      instance[key] = value;
    });

    return modalRef;
  }

  public openEntityModalWithDocument<T>(
    document: Document,          // instance of document
    actualCompany: Company,
    change: EntityChangeData,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {}
  ): NgbModalRef {

    switch (document.type[0]) {
      case 'c':
        return this.openModalWithDocument(document, actualCompany, change, props, modalOptions);
      case 't':
        return this.openTrustModalWithDocument(document, actualCompany, change, props, modalOptions);
      default: {
        if (document.type == CompanyDividendStatement.$type) {
          return this.openModalWithDocument(document, actualCompany, change, props, modalOptions);
        }

        throw new Error('Unknown Document type: ' + document.type);
      }
    }
  }

  public closeAllFormsModals(): void {
    this.openedForms = this.openedForms.filter((ref) => ref.componentInstance);

    if (!this.openedForms.length)
      return;

    this.openedForms.forEach((form) => {
      form.componentInstance['forceClose'] = true;
      form.dismiss();
    });
    this.modalService.dismissAll();
  }

  public get modalOpened(): Signal<boolean> {
    return this._modalOpened.asReadonly();
  }
}
